import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Chess } from 'chess.js';
import { Chessboard } from 'react-chessboard';
import { requestChessyBotMove } from '../../lib/chessyGateway';
import ChessyBrand from '../ChessyBrand';

const FORCE_LEGACY_LOCAL_ENGINE = true;
const SHOW_ENGINE_FALLBACK_NOTICE = process.env.NODE_ENV !== 'production';
const SPLINE_VIEWER_SRC = 'https://unpkg.com/@splinetool/viewer@1.9.82/build/spline-viewer.js';
const SPLINE_SCENE_URL = 'https://prod.spline.design/kZDDjO5HuC9GJUM2/scene.splinecode';

function fileToNumber(file) {
  const ch = String(file || '').toLowerCase();
  const code = ch.charCodeAt(0);
  if (code < 97 || code > 104) return 0;
  return code - 96;
}

function moveHeuristicScore(move) {
  let score = 0;
  const san = String(move?.san || '');
  if (san.includes('#')) score += 5000;
  if (san.includes('+')) score += 400;
  if (move?.captured) score += 300;
  if (move?.promotion) score += 500;
  if (String(move?.flags || '').includes('k') || String(move?.flags || '').includes('q')) score += 60;

  const to = String(move?.to || '').toLowerCase();
  const file = fileToNumber(to[0]);
  const rank = Number(to[1] || 0);
  const centerBias = 8 - Math.abs(4.5 - file) - Math.abs(4.5 - rank);
  score += Math.max(0, Math.round(centerBias * 10));
  return score;
}

function parseLevelKey(level) {
  const key = String(level || 'beginner').toLowerCase();
  if (key === 'expert' || key === 'advanced' || key === 'intermediate') return key;
  return 'beginner';
}

function pickLegacyLocalMoveFromFen(fen, level) {
  const chess = new Chess();
  chess.load(fen);
  const legal = chess.moves({ verbose: true }) || [];
  if (!legal.length) {
    throw new Error('No legal moves available');
  }

  const scored = legal
    .map((m) => ({
      move: m,
      score: moveHeuristicScore(m) + Math.floor(Math.random() * 50)
    }))
    .sort((a, b) => b.score - a.score);

  const levelKey = parseLevelKey(level);
  let poolSize = scored.length;
  if (levelKey === 'expert') poolSize = Math.min(scored.length, 1);
  if (levelKey === 'advanced') poolSize = Math.min(scored.length, 2);
  if (levelKey === 'intermediate') poolSize = Math.min(scored.length, Math.max(2, Math.ceil(scored.length * 0.35)));
  if (levelKey === 'beginner') poolSize = Math.min(scored.length, Math.max(3, Math.ceil(scored.length * 0.7)));

  const candidatePool = scored.slice(0, Math.max(1, poolSize));
  const picked = candidatePool[Math.floor(Math.random() * candidatePool.length)]?.move || scored[0].move;
  return {
    from: String(picked.from || '').trim().toLowerCase(),
    to: String(picked.to || '').trim().toLowerCase(),
    promotion: String(picked.promotion || '').trim().toLowerCase() || null,
    san: String(picked.san || '').trim(),
    engine: 'legacy-local'
  };
}

export default function ChessyPlayground({ language = 'ro' }) {
  const miniCardRef = useRef(null);
  const [game, setGame] = useState(() => new Chess());
  const [isBotThinking, setIsBotThinking] = useState(false);
  const [lastSan, setLastSan] = useState('');
  const [moveHistory, setMoveHistory] = useState([]);
  const [viewPly, setViewPly] = useState(0);
  const [selectedArrowPly, setSelectedArrowPly] = useState(0);
  const [moveHints, setMoveHints] = useState({});
  const [illegalMoveNotice, setIllegalMoveNotice] = useState('');
  const [engineNotice, setEngineNotice] = useState('');
  const engineLevel = 'expert';

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    if (window.customElements?.get('spline-viewer')) return undefined;
    const existing = document.querySelector('script[data-spline-viewer="true"]');
    if (existing) return undefined;
    const script = document.createElement('script');
    script.type = 'module';
    script.src = SPLINE_VIEWER_SRC;
    script.setAttribute('data-spline-viewer', 'true');
    document.head.appendChild(script);
    return undefined;
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const viewer = miniCardRef.current?.querySelector('spline-viewer');
    if (!viewer) return undefined;

    const hideSplineBrand = () => {
      try {
        const sr = viewer.shadowRoot;
        if (!sr) return;
        const style = document.createElement('style');
        style.textContent = '#logo{display:none!important}a[href*="spline.design"]{display:none!important}[class*="logo"]{display:none!important}[id*="watermark"]{display:none!important}';
        sr.appendChild(style);
      } catch {
        // noop
      }
    };

    viewer.addEventListener('load', hideSplineBrand);
    window.setTimeout(hideSplineBrand, 1200);
    window.setTimeout(hideSplineBrand, 3200);
    return () => viewer.removeEventListener('load', hideSplineBrand);
  }, []);

  const boardFen = useMemo(() => {
    if (viewPly <= 0) return 'start';
    return moveHistory[viewPly - 1]?.fenAfter || game.fen();
  }, [viewPly, moveHistory, game]);
  const legalTurn = game.turn();
  const isGameOver = game.isGameOver();
  const isLiveView = viewPly === moveHistory.length;
  const statusText = useMemo(() => {
    if (isBotThinking) {
      return language === 'ro' ? 'Chessy AI gândește...' : 'Chessy AI is thinking...';
    }
    if (!isLiveView) {
      return language === 'ro'
        ? 'Ești în modul review. Apasă Live ca să continui partida.'
        : 'You are in review mode. Press Live to continue the game.';
    }
    if (game.isCheckmate()) return language === 'ro' ? 'Șah mat.' : 'Checkmate.';
    if (game.isDraw()) return language === 'ro' ? 'Remiză.' : 'Draw.';
    if (game.inCheck()) return language === 'ro' ? 'Șah.' : 'Check.';
    return language === 'ro' ? 'Joacă o mutare contra lui Chessy AI.' : 'Play a move against Chessy AI.';
  }, [game, language, isBotThinking, isLiveView]);

  const checkAlertText = useMemo(() => {
    if (!isLiveView || isBotThinking) return '';
    if (!game.inCheck() || game.isCheckmate()) return '';
    return language === 'ro'
      ? 'Șah'
      : 'Check';
  }, [game, language, isLiveView, isBotThinking]);

  const checkmateAlertText = useMemo(() => {
    if (!isLiveView || isBotThinking) return '';
    if (!game.isCheckmate()) return '';
    const winner = game.turn() === 'w' ? 'black' : 'white';
    if (language === 'ro') {
      return winner === 'white' ? 'Șah mat · Albul a câștigat' : 'Șah mat · Negrul a câștigat';
    }
    return winner === 'white' ? 'Checkmate · White wins' : 'Checkmate · Black wins';
  }, [game, language, isLiveView, isBotThinking]);

  const groupedMoves = useMemo(() => {
    const rows = [];
    for (let i = 0; i < moveHistory.length; i += 2) {
      rows.push({
        moveNo: Math.floor(i / 2) + 1,
        white: moveHistory[i] || null,
        black: moveHistory[i + 1] || null
      });
    }
    return rows;
  }, [moveHistory]);

  const boardArrows = useMemo(() => {
    const ply = selectedArrowPly || viewPly;
    if (ply < 1 || ply > moveHistory.length) return [];
    const move = moveHistory[ply - 1];
    if (!move?.from || !move?.to) return [];
    return [[move.from, move.to, 'rgba(37,99,235,0.85)']];
  }, [selectedArrowPly, viewPly, moveHistory]);

  const onSquareClick = (square) => {
    if (isBotThinking || isGameOver || legalTurn !== 'w' || !isLiveView) return;
    const sq = String(square || '').trim().toLowerCase();
    if (!sq) return;

    const piece = game.get(sq);
    if (!piece || piece.color !== 'w') {
      setMoveHints({});
      return;
    }

    const legal = game.moves({ square: sq, verbose: true }) || [];
    if (!legal.length) {
      setMoveHints({});
      return;
    }

    const hints = {
      [sq]: {
        background: 'radial-gradient(circle, rgba(59,130,246,0.3) 32%, rgba(59,130,246,0.12) 70%)',
        boxShadow: 'inset 0 0 0 2px rgba(59,130,246,0.72)'
      }
    };

    legal.forEach((m) => {
      hints[m.to] = {
        background: m.captured
          ? 'radial-gradient(circle, rgba(239,68,68,0.95) 0 18%, rgba(239,68,68,0.34) 19% 46%, rgba(239,68,68,0.12) 47% 100%)'
          : 'radial-gradient(circle, rgba(37,99,235,0.92) 0 16%, rgba(34,197,94,0.32) 17% 42%, rgba(34,197,94,0.1) 43% 100%)',
        boxShadow: m.captured
          ? 'inset 0 0 0 2px rgba(239,68,68,0.65)'
          : 'inset 0 0 0 2px rgba(34,197,94,0.65)'
      };
    });

    setMoveHints(hints);
  };

  const runBotMove = async (nextChess, baseHistory) => {
    const next = new Chess(nextChess.fen());
    if (next.isGameOver()) {
      setGame(next);
      return;
    }
    if (next.turn() !== 'b') {
      setGame(next);
      return;
    }
    // Keep player move visible immediately before bot reply.
    setGame(new Chess(next.fen()));
    setIsBotThinking(true);
    setEngineNotice('');

    try {
      const chosen = FORCE_LEGACY_LOCAL_ENGINE
        ? pickLegacyLocalMoveFromFen(next.fen(), engineLevel)
        : await requestChessyBotMove({
            fen: next.fen(),
            level: engineLevel
          });

      if (String(chosen?.engine || '').toLowerCase() === 'legacy-local') {
        setEngineNotice(
          SHOW_ENGINE_FALLBACK_NOTICE
            ? (language === 'ro'
                ? 'Rulează engine-ul local legacy (temporar).'
                : 'Legacy local engine is active (temporary).')
            : ''
        );
      } else if (String(chosen?.engine || '').toLowerCase() === 'legacy-fallback') {
        setEngineNotice(
          SHOW_ENGINE_FALLBACK_NOTICE
            ? (language === 'ro'
                ? 'Viridithas indisponibil: rulează fallback local (temporar).'
                : 'Viridithas unavailable: local fallback engine active (temporary).')
            : ''
        );
      }
      if (!chosen?.from || !chosen?.to) {
        setEngineNotice(
          language === 'ro'
            ? 'Motor indisponibil: răspuns invalid.'
            : 'Engine unavailable: invalid response.'
        );
        setIsBotThinking(false);
        return;
      }

      let botMove = null;
      try {
        botMove = next.move({
          from: chosen.from,
          to: chosen.to,
          promotion: chosen.promotion || 'q'
        });
      } catch {
        botMove = null;
      }

      if (!botMove) {
        setEngineNotice(
          language === 'ro'
            ? 'Motor indisponibil: mutare invalidă.'
            : 'Engine unavailable: invalid move.'
        );
        setIsBotThinking(false);
        return;
      }

      const botEntry = {
        ply: Number(baseHistory.length || 0) + 1,
        color: 'b',
        san: botMove.san,
        from: botMove.from,
        to: botMove.to,
        fenAfter: next.fen()
      };
      const fullHistory = [...baseHistory, botEntry];
      setMoveHistory(fullHistory);
      setViewPly(fullHistory.length);
      setSelectedArrowPly(fullHistory.length);
      setLastSan(botMove.san);
      setGame(new Chess(next.fen()));
      setMoveHints({});
      setIsBotThinking(false);
    } catch {
      try {
        const chosen = pickLegacyLocalMoveFromFen(next.fen(), engineLevel);
        let botMove = null;
        try {
          botMove = next.move({
            from: chosen.from,
            to: chosen.to,
            promotion: chosen.promotion || 'q'
          });
        } catch {
          botMove = null;
        }

        if (!botMove) {
          setEngineNotice(
            language === 'ro'
              ? 'Motor indisponibil momentan.'
              : 'Engine currently unavailable.'
          );
          setIsBotThinking(false);
          return;
        }

        const botEntry = {
          ply: Number(baseHistory.length || 0) + 1,
          color: 'b',
          san: botMove.san,
          from: botMove.from,
          to: botMove.to,
          fenAfter: next.fen()
        };
        const fullHistory = [...baseHistory, botEntry];
        setMoveHistory(fullHistory);
        setViewPly(fullHistory.length);
        setSelectedArrowPly(fullHistory.length);
        setLastSan(botMove.san);
        setGame(new Chess(next.fen()));
        setMoveHints({});
        setEngineNotice(
          SHOW_ENGINE_FALLBACK_NOTICE
            ? (language === 'ro'
                ? 'Rulează engine-ul local legacy (temporar).'
                : 'Legacy local engine is active (temporary).')
            : ''
        );
        setIsBotThinking(false);
        return;
      } catch {
        setEngineNotice(
          language === 'ro'
            ? 'Motor indisponibil momentan.'
            : 'Engine currently unavailable.'
        );
        setIsBotThinking(false);
      }
    }
  };

  const jumpToPly = (ply) => {
    const safe = Math.max(0, Math.min(moveHistory.length, Number(ply || 0)));
    setViewPly(safe);
    setSelectedArrowPly(safe);
    setMoveHints({});
  };

  const onPieceDrop = (sourceSquare, targetSquare) => {
    if (isBotThinking || isGameOver || legalTurn !== 'w' || !isLiveView) return false;
    const next = new Chess(game.fen());
    let move = null;
    try {
      move = next.move({
        from: sourceSquare,
        to: targetSquare,
        promotion: 'q'
      });
    } catch {
      move = null;
    }
    if (!move) {
      setIllegalMoveNotice(language === 'ro' ? 'Mutare ilegală' : 'Illegal move');
      window.setTimeout(() => setIllegalMoveNotice(''), 1200);
      return false;
    }
    setIllegalMoveNotice('');
    const playerEntry = {
      ply: moveHistory.length + 1,
      color: 'w',
      san: move.san || '',
      from: move.from,
      to: move.to,
      fenAfter: next.fen()
    };
    const afterPlayer = [...moveHistory, playerEntry];
    setLastSan(move.san || '');
    setGame(new Chess(next.fen()));
    setMoveHistory(afterPlayer);
    setViewPly(afterPlayer.length);
    setSelectedArrowPly(afterPlayer.length);
    setMoveHints({});
    runBotMove(next, afterPlayer);
    return true;
  };

  const resetGame = () => {
    setGame(new Chess());
    setLastSan('');
    setIsBotThinking(false);
    setMoveHistory([]);
    setViewPly(0);
    setSelectedArrowPly(0);
    setMoveHints({});
    setIllegalMoveNotice('');
    setEngineNotice('');
  };

  return (
    <section className="module-card chessy-playground-card">
      <div className="module-header">
        <div>
          <p className="module-index">{language === 'ro' ? 'Playground' : 'Playground'}</p>
          <h3 className="module-title chessy-playground-title">
            <span className="chessy-queen-icon" aria-hidden="true">♛</span>
            <ChessyBrand />
          </h3>
          <p className="module-summary">
            {language === 'ro'
              ? 'Antrenează-te contra lui Chessy AI într-un duel strategic continuu: ritm, calcul și precizie.'
              : 'Train against Chessy AI in a continuous strategic duel: pace, calculation, and precision.'}
          </p>
          <div className="chessy-beta-badge" role="note">
            <span className="chessy-beta-dot" aria-hidden="true">●</span>
            <span>
              <ChessyBrand className="chessy-beta-brand" />{' '}
              {language === 'ro'
                ? 'este la început: încă învață, poate greși uman și devine mai bună cu fiecare versiune.'
                : 'is in early training: it can make human-like mistakes and improves with each version.'}
            </span>
          </div>
        </div>
        <article className="chessy-playground-mini-card" ref={miniCardRef}>
          <div className="chessy-playground-mini-left">
            <h4><ChessyBrand /></h4>
            <p>{language === 'ro' ? 'Antrenor de joc' : 'Game coach'}</p>
          </div>
          <div className="chessy-playground-mini-right">
            <div className="chessy-playground-mini-spline-wrap">
              <spline-viewer url={SPLINE_SCENE_URL} />
            </div>
            <div className="chessy-playground-mini-mask-t" />
            <div className="chessy-playground-mini-mask-b" />
            <div className="chessy-playground-mini-mask-r" />
            <div className="chessy-playground-mini-mask-l" />
          </div>
        </article>
      </div>

      <div className="chessy-playground-toolbar">
        <button className="btn-start-lesson mini" onClick={resetGame}>
          {language === 'ro' ? 'Reset partidă' : 'Reset game'}
        </button>
      </div>

      <div className="chessy-playground-status">
        <span>{statusText}</span>
        <span>
          {language === 'ro' ? 'Ultima mutare' : 'Last move'}: <strong>{lastSan || '—'}</strong>
        </span>
        {illegalMoveNotice && (
          <span style={{ color: '#b91c1c', fontWeight: 800 }}>{illegalMoveNotice}</span>
        )}
        {engineNotice && (
          <span style={{ color: '#b45309', fontWeight: 800 }}>{engineNotice}</span>
        )}
      </div>
      {!!checkAlertText && (
        <div className="chessy-game-alert check" role="status" aria-live="polite">
          ♚ {checkAlertText}
        </div>
      )}
      {!!checkmateAlertText && (
        <div className="chessy-game-alert checkmate" role="status" aria-live="polite">
          ✨ {checkmateAlertText}
        </div>
      )}

      <div className="chessy-playground-layout">
        <aside className="chessy-moves-panel">
          <div className="chessy-moves-head">
            <strong>{language === 'ro' ? 'Mutări' : 'Moves'}</strong>
            <div className="chessy-moves-nav">
              <button type="button" onClick={() => jumpToPly(viewPly - 1)} disabled={viewPly <= 0}>
                ←
              </button>
              <button type="button" onClick={() => jumpToPly(viewPly + 1)} disabled={viewPly >= moveHistory.length}>
                →
              </button>
              <button type="button" onClick={() => jumpToPly(moveHistory.length)} disabled={isLiveView}>
                Live
              </button>
            </div>
          </div>
          <div className="chessy-moves-list" role="list">
            {groupedMoves.map((row) => (
              <div className="chessy-move-row" key={`row-${row.moveNo}`}>
                <span className="chessy-move-no">{row.moveNo}.</span>
                <button
                  type="button"
                  className={`chessy-move-btn ${selectedArrowPly === row.white?.ply ? 'selected' : ''}`}
                  onClick={() => row.white?.ply && jumpToPly(row.white.ply)}
                  disabled={!row.white}
                >
                  {row.white?.san || '—'}
                </button>
                <button
                  type="button"
                  className={`chessy-move-btn ${selectedArrowPly === row.black?.ply ? 'selected' : ''}`}
                  onClick={() => row.black?.ply && jumpToPly(row.black.ply)}
                  disabled={!row.black}
                >
                  {row.black?.san || '—'}
                </button>
              </div>
            ))}
          </div>
        </aside>

        <div className="chessy-board-wrap">
          <Chessboard
            id="chessy-playground-board"
            position={boardFen}
            boardOrientation="white"
            onSquareClick={onSquareClick}
            onPieceDrop={onPieceDrop}
            arePiecesDraggable={!isBotThinking && !isGameOver && legalTurn === 'w' && isLiveView}
            customArrows={boardArrows}
            customSquareStyles={moveHints}
            customBoardStyle={{ borderRadius: 14, boxShadow: '0 12px 34px rgba(15,23,42,0.16)' }}
          />
        </div>
      </div>
    </section>
  );
}
